import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FieldHint, FieldLabel } from "../shared/FieldHint";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUSES = ["New", "Contacted", "Closed", "Dead"];
const PRODUCTS = [{ value: "funding", label: "Business Funding" }, { value: "trust", label: "Trust & Asset Protection" }];
const TRUST_PRODUCTS = [{ value: "basic", label: "Basic Living Trust ($1,497)" }, { value: "private", label: "Private Trust ($2,497)" }];

export function ManagerLeadInboxTab() {
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ name: "", product_interest: "funding", phone: "", email: "", deal_value: "", trust_product: "basic" });
  const [submitting, setSubmitting] = useState(false);

  const fetchLeads = async () => {
    try {
      const { data } = await api.get("/manager/lead-inbox");
      setLeads(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const addLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/manager/lead-inbox", { ...form, deal_value: parseFloat(form.deal_value) || 0, trust_product: form.product_interest === "trust" ? form.trust_product : null });
      toast.success("Lead added to your inbox");
      setForm({ name: "", product_interest: "funding", phone: "", email: "", deal_value: "", trust_product: "basic" });
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/manager/lead-inbox/${id}/status`, { status });
      toast.success("Status updated");
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="manager-lead-inbox-tab">
      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4" title="Use this form to manually add a lead you already have — from your own network, a walk-in, or anywhere outside the normal lead pipeline.">Add a Lead Manually</h2>
      <form onSubmit={addLead} className="flex items-end gap-2 flex-wrap mb-2 bg-igc-surface border border-igc-gold/15 p-4">
        <Input data-testid="manager-lead-name-input" placeholder="Lead name" title="The full name of the person or business you're adding as a lead." value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-44 h-9 text-xs" />
        <Select value={form.product_interest} onValueChange={(v) => setForm({ ...form, product_interest: v })}>
          <SelectTrigger data-testid="manager-lead-product-select" title="Which IGC product this lead is interested in — Business Funding or Trust & Asset Protection." className="w-48 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {form.product_interest === "trust" && (
          <Select value={form.trust_product} onValueChange={(v) => setForm({ ...form, trust_product: v })}>
            <SelectTrigger data-testid="manager-lead-trust-product-select" title="Which specific type of trust this lead wants — Basic Living Trust or Private Trust, each with a different fixed commission." className="w-52 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              {TRUST_PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input data-testid="manager-lead-phone-input" placeholder="Phone" title="A working phone number to reach this lead." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-36 h-9 text-xs" />
        <Input data-testid="manager-lead-email-input" placeholder="Email" title="A working email address for this lead." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-48 h-9 text-xs" />
        <div className="flex items-center gap-1">
          <Input data-testid="manager-lead-deal-value-input" type="number" min="0" placeholder="Estimated deal value" title="Your best guess at the total dollar value of this deal — helps prioritize follow-up." value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs" />
          <FieldHint text="Enter your best guess at how much funding this client is looking for. You can update this anytime." testId="manager-lead-deal-value-hint" />
        </div>
        <Button data-testid="manager-lead-add-button" type="submit" disabled={submitting} title="Saves this lead into your inbox below so you can track and work it." className="h-9 text-xs bg-igc-gold hover:bg-igc-goldHover text-black">Add Lead</Button>
      </form>
      <p className="text-igc-muted text-[11px] mb-10">Trust leads that close automatically get their fixed commission credited to you.</p>

      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4" title="Every lead sent to you by admin, or that you manually added above, lives here so you can track and work them.">Your Lead Inbox</h2>
      {leads.length === 0 && <p className="text-igc-muted text-sm" data-testid="manager-lead-inbox-empty">No leads in your inbox yet — pushed leads from admin or your own manual entries will appear here.</p>}
      <div className="space-y-2">
        {leads.map((l) => (
          <div key={l.id} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`manager-lead-row-${l.id}`}>
            <div>
              <p className="text-igc-cream text-sm">{l.name} <span className="text-igc-muted text-xs">({l.source === "funding" ? "Business Funding" : "Trust & Asset Protection"})</span></p>
              <p className="text-igc-muted text-xs" title="Whether this lead came from admin or was entered by you, and when it showed up.">{l.contact || "no contact info"} — {l.manager_lead_source === "admin_assigned" ? "Assigned by admin" : "Self-entered"} — {formatET(l.created_at, "MMM d, yyyy")}</p>
              {l.deal_value > 0 && <p className="text-igc-goldText text-xs mt-0.5" data-testid={`manager-lead-deal-value-${l.id}`} title="The estimated dollar value of this deal, either entered manually or provided by admin.">Est. value ${l.deal_value.toLocaleString()}</p>}
            </div>
            <div className="flex items-center gap-1">
              <FieldLabel hint="Where this deal currently stands in the process. Update this as things move forward." testId={`manager-lead-status-hint-${l.id}`} />
              <Select value={l.stage} onValueChange={(v) => updateStatus(l.id, v)}>
                <SelectTrigger data-testid={`manager-lead-status-select-${l.id}`} title="Update this lead's status — New, Contacted, Closed, or Dead — as you work it." className="w-32 h-8 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
